import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "search-container" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["rows", "total-items", "current-page", "loading"]

import { EPINIO_TYPES } from '../types';
import { useStore } from 'vuex';
import { computed, ref, watch } from 'vue';
import Masthead from '@shell/components/ResourceList/Masthead';
import { debounce } from 'lodash';
import ChartsModal from '../components/charts/ChartsModal.vue';
import { makeActionMenu } from '../utils/table-formatters';
import ChartsDeleteModal from '../components/charts/ChartsDeleteModal.vue';
import { useAppCharts } from '../queries/useAppChartsQueries';
import { ListResourceRequestParams, ResourceQueryOptions, ResourceTableRow } from '../models/resource/ui-types';
import { AppChart } from '../models/appcharts/ui-types';
import Banner from '@components/Banner/Banner.vue';
import { useUser } from '../queries/useUserQueries';


export default /*@__PURE__*/_defineComponent({
  __name: 'appcharts',
  props: {
    schema: {}
  },
  setup(__props: any) {

 // Keep for compatibility

const store = useStore();

const chartsModal = ref<InstanceType<typeof ChartsModal> | null>(null);
const deleteModal = ref<InstanceType<typeof ChartsDeleteModal> | null>(null);

const resource: string = EPINIO_TYPES.APP_CHARTS;

const { data: user, isError: isErrorUser, error: userError } = useUser(store);

const requestParams = ref<ListResourceRequestParams>({
  page: 1,
  pageSize: 10,
  search: '',
});

const requestOptions = ref<ResourceQueryOptions>({
  enabled: true,
  polling: true,
  isTablePagination: true,
});

const searchQuery = ref<string>('');

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

const onSearch = debounce(async (query: string) => {
  requestParams.value.page = 1;
  requestParams.value.search = query;
}, 500);

const {data: appCharts, isLoading: isLoadingAppCharts, isError: isErrorAppCharts, error: appChartsError} = useAppCharts(store, requestParams, requestOptions);

const canEdit = computed(() => {
  return user.value?.permissions?.chart_write;
});
const canDelete = canEdit;
const canCreate = canEdit;

const openDeleteModal = (appChart: AppChart) => {
  deleteModal.value?.openDelete(appChart);
};

const openEditModal = (appChart: AppChart) => {
  chartsModal.value?.openEdit(appChart);
};

const displayRows = computed(() => {
  if (!appCharts.value) {
    return [];
  }

  const rows: ResourceTableRow<AppChart>[] = (appCharts.value.items ?? []).map((ac) => ({
    ...ac,
    id: ac.meta.name, // stable, unique per namespace
    availableActions: [
      {
        label: 'Edit',
        action: () => openEditModal(ac),
        enabled: canEdit.value,
        visible: canEdit.value,
      },
      {
        label: 'Delete',
        action: () => openDeleteModal(ac),
        enabled: canDelete.value,
        visible: canDelete.value,
        danger: true,
      },
    ],
    canDelete: canDelete.value,
  }));
  return rows;
});

const columns = [
  {
    field: 'nameDisplay',
    label: 'Name',
    formatter: (_v: any, row: AppChart) => {
      const el = document.createElement('a');

      el.textContent = row.meta?.name || '';
      el.style.cursor = 'pointer';
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        chartsModal.value?.openView(row, !!canEdit.value);
      });

      return el;
    }
  },
  {
    field: 'description',
    label: 'Description'
  },
  {
    field: 'helmChart',
    label: 'Helm Chart'
  },
  {
    field:     'meta.createdAt',
    label:     'Age',
    formatter: 'age'
  }
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(Masthead), {
        schema: _ctx.schema,
        resource: _unref(resource)
      }, {
        createButton: _withCtx(() => [
          (_unref(canCreate))
            ? (_openBlock(), _createElementBlock("trailhand-button", {
                key: 0,
                variant: "primary",
                size: "large",
                onButtonClick: _cache[0] || (_cache[0] = ($event: any) => (chartsModal.value?.openCreate()))
              }, _toDisplayString(_ctx.t('generic.create')), 33))
            : (_openBlock(), _createElementBlock("div", _hoisted_2))
        ]),
        _: 1
      }, 8, ["schema", "resource"]),
      _cache[3] || (_cache[3] = _createTextVNode()),
      (_unref(isErrorUser))
        ? (_openBlock(), _createBlock(Banner, {
            key: 0,
            color: "error",
            label: _unref(userError)?.message || _ctx.t('epinio.user.errors.fetch')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true),
      _cache[4] || (_cache[4] = _createTextVNode()),
      (_unref(isErrorAppCharts))
        ? (_openBlock(), _createBlock(Banner, {
            key: 1,
            color: "error",
            label: _unref(appChartsError)?.message || _ctx.t('epinio.appCharts.errors.fetch')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true),
      _cache[5] || (_cache[5] = _createTextVNode()),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("trailhand-text-input", {
          value: searchQuery.value,
          placeholder: "Search...",
          onTextInputChange: _cache[1] || (_cache[1] = (e) => { requestParams.value.search = e.detail.value; })
        }, null, 40, _hoisted_4)
      ]),
      _cache[6] || (_cache[6] = _createTextVNode()),
      _createElementVNode("trailhand-table", {
        ref: (el) => { if (el) el.renderActions = _unref(makeActionMenu); },
        rows: displayRows.value,
        columns: columns,
        "server-side": true,
        searchable: false,
        "total-items": _unref(appCharts)?.totalItems ?? 0,
        "current-page": requestParams.value.page,
        loading: _unref(isLoadingAppCharts),
        "key-field": "id",
        onPageChange: _cache[2] || (_cache[2] = (e) => { requestParams.value.page = e.detail.page; })
      }, null, 40, _hoisted_5)
    ]),
    _cache[7] || (_cache[7] = _createTextVNode()),
    _createVNode(ChartsModal, {
      ref_key: "chartsModal",
      ref: chartsModal
    }, null, 512),
    _cache[8] || (_cache[8] = _createTextVNode()),
    _createVNode(ChartsDeleteModal, {
      ref_key: "deleteModal",
      ref: deleteModal
    }, null, 512)
  ], 64))
}
}

})